import React from "react";

import "@knight-lab/timelinejs/dist/css/timeline.css";
import { publicUrl } from "../../config";
import { toQuarter } from "../../date";
import { useSearchParamState } from "../../hooks/use-search-param-state";
import { Item } from "../../model";
import { toTimelineEvents } from "../../timelineEvents";
import "./release-timeline.scss";

type ReleaseTimelineProps = {
  /** Every published release, ascending. Empty releases cannot exist (see radar:validate). */
  releases: string[];
  /** The release currently displayed. */
  selected: string;
  /** All items of the radar, unfiltered by release, used to summarise each quarter. */
  items: Item[];
  /** Distinguishes the two radars, so switching radar rebuilds the widget. */
  radarSlug: string;
};

type TimelineInstance = {
  current_id?: string;
  goToId: (id: string) => void;
  on: (event: string, fn: (data: { unique_id?: string }) => void) => void;
  off?: (event: string, fn: (data: { unique_id?: string }) => void) => void;
};

/** Where `copy-timelinejs.js` put the bundle. Same origin, never the Knight Lab CDN. */
const SCRIPT_PATH = `${publicUrl}vendor/timelinejs/`;
const SCRIPT_URL = `${SCRIPT_PATH}timeline.js`;

let loader: Promise<any> | null = null;

/**
 * Loads the TimelineJS bundle once.
 *
 * It is shipped as a `var TL = ...` global-library build: webpack would scope
 * that variable to a module and make it unreachable, so it is injected as a
 * classic script instead.
 */
const loadTimelineJs = (): Promise<any> => {
  if ((window as any).TL) {
    return Promise.resolve((window as any).TL);
  }
  if (loader) {
    return loader;
  }

  loader = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-timelinejs]`
    );
    const script = existing || document.createElement("script");
    script.dataset.timelinejs = "true";
    script.async = true;
    script.onload = () => resolve((window as any).TL);
    script.onerror = () => reject(new Error(`failed to load ${SCRIPT_URL}`));
    if (!existing) {
      script.src = SCRIPT_URL;
      document.head.appendChild(script);
    }
  });

  return loader;
};

const Chevron = ({ direction }: { direction: "left" | "right" }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d={direction === "left" ? "m14 6-6 6 6 6" : "m10 6 6 6-6 6"} />
  </svg>
);

const ReleaseTimeline: React.FC<ReleaseTimelineProps> = ({
  releases,
  selected,
  items,
  radarSlug,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const timelineRef = React.useRef<TimelineInstance | null>(null);
  const [, setSearchParamsState] = useSearchParamState();

  const current = releases[releases.length - 1];
  const isCurrent = selected === current;

  // Read inside the widget's event handler, which is created once and must not
  // close over stale values.
  const selectedRef = React.useRef(selected);
  selectedRef.current = selected;
  const currentRef = React.useRef(current);
  currentRef.current = current;
  const setParamsRef = React.useRef(setSearchParamsState);
  setParamsRef.current = setSearchParamsState;

  const data = React.useMemo(
    () => toTimelineEvents(items, releases),
    [items, releases]
  );
  const dataRef = React.useRef(data);
  dataRef.current = data;

  /*
    Instantiated once per (radar, release set). Rebuilding the widget on every
    render would be expensive and would reset the viewport, so `selected`
    changes are applied with `goToId` in the effect below instead.
  */
  const instanceKey = `${radarSlug}:${releases.length}`;

  React.useEffect(() => {
    let disposed = false;
    const container = containerRef.current;

    const onChange = (event: { unique_id?: string }) => {
      const id = event && event.unique_id;
      if (!id) {
        return;
      }
      /*
        Feedback-loop guard. `goToId` fires `change` itself, so the two
        directions are kept idempotent by comparing against the state we already
        want: if the widget reports the release we are already on, there is
        nothing to write, and the loop cannot start.
      */
      if (id === selectedRef.current) {
        return;
      }
      setParamsRef.current({
        release: id === currentRef.current ? undefined : id,
      });
    };

    loadTimelineJs()
      .then((TL) => {
        if (disposed || !container || !TL) {
          return;
        }

        const startIndex = Math.max(
          0,
          dataRef.current.events.findIndex(
            (event) => event.unique_id === selectedRef.current
          )
        );

        const timeline: TimelineInstance = new TL.Timeline(
          container,
          dataRef.current,
          {
            // Everything below that could reach the network is switched off:
            // `font` would pull font.default.css from script_path and, through
            // it, fonts.gstatic.com; `theme` and `soundcite` load extra CSS;
            // the ga_* options inject Google Analytics.
            font: null,
            theme: null,
            soundcite: false,
            ga_measurement_id: null,
            ga_property_id: null,
            // "en" is compiled into the bundle, so no locale file is fetched.
            language: "en",
            // Only used to resolve assets, and it points at our own origin.
            script_path: SCRIPT_PATH,
            hash_bookmark: false,
            /*
              Scale is fixed. `optimal_tick_width` drives how many quarters fit
              on screen (about six or seven at 100px), and the zoom controls are
              hidden in CSS, so the scale the user sees is the only scale there
              is. Navigating pans the axis, it never rezooms it.
            */
            initial_zoom: 1,
            optimal_tick_width: 100,
            zoom_sequence: [1],
            // The axis and the slide were measured in the browser: the stock
            // 150/278 split spent 400px on ~100px of text. The slide is laid
            // out in two columns now, so both can shrink.
            timenav_height: 110,
            timenav_height_min: 100,
            marker_height_min: 22,
            // Default is 100px each side, which pushed the quarter label 100px
            // in before TimelineJS's own JS-computed centring added another 100.
            slide_padding_lr: 24,
            duration: 400,
            start_at_slide: startIndex,
          }
        );

        timelineRef.current = timeline;
        timeline.on("change", onChange);

        /*
          Deep links. The widget loads asynchronously, so the `selected` effect
          below has already run - and bailed out - by the time the instance
          exists. Without this correction, opening `?release=2024-07-01`
          filtered the radar correctly while the slide stayed on Q2 2023.

          Deferred by one frame on purpose: calling `goToId` synchronously after
          the constructor races the widget's own initial layout, and the axis
          stayed parked on the first quarter with the selected marker off
          screen. One frame later the axis pans to the marker as it should.

          `goToId` fires `change`, but `onChange` returns early when the id is
          the one we already want, so this cannot start a loop.
        */
        window.requestAnimationFrame(() => {
          if (disposed || timelineRef.current !== timeline) {
            return;
          }
          if (timeline.current_id !== selectedRef.current) {
            timeline.goToId(selectedRef.current);
          }
        });
      })
      .catch((error) => {
        console.error("TimelineJS failed to load", error);
      });

    return () => {
      disposed = true;
      const timeline = timelineRef.current;
      if (timeline && timeline.off) {
        timeline.off("change", onChange);
      }
      timelineRef.current = null;
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [instanceKey]);

  /** URL -> widget. `goToId` is itself a no-op when already on that slide. */
  React.useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline || timeline.current_id === selected) {
      return;
    }
    timeline.goToId(selected);
  }, [selected]);

  if (releases.length === 0) {
    return null;
  }

  const index = releases.indexOf(selected);
  const previous = index > 0 ? releases[index - 1] : undefined;
  const next =
    index >= 0 && index < releases.length - 1 ? releases[index + 1] : undefined;

  /*
    Quarter to quarter navigation goes through the URL, like every other way of
    changing quarter: the `selected` effect above then pans the widget. Writing
    to the widget directly would leave `?release=` behind.
  */
  const goTo = (release?: string) => {
    if (!release) {
      return;
    }
    setSearchParamsState({ release: release === current ? undefined : release });
  };

  return (
    <div className="release-timeline">
      {/*
        The mount node is a child, not this element: TimelineJS overwrites
        `container.className` wholesale (Timeline.js:678), which would wipe our
        own class off it.
      */}
      <div
        className="release-timeline__widget"
        role="group"
        aria-label="Published quarters"
      >
        <div className="release-timeline__mount" ref={containerRef} />

        {/*
          Our own arrows, pinned to the edges of the slide. TimelineJS ships a
          pair (`tl-slidenav-*`) but positions them from JavaScript in the middle
          of the slide, over the text.
        */}
        <button
          type="button"
          className="release-timeline__nav release-timeline__nav--prev"
          onClick={() => goTo(previous)}
          disabled={!previous}
          aria-label={
            previous
              ? `Earlier quarter, ${toQuarter(previous)}`
              : "No earlier quarter"
          }
        >
          <Chevron direction="left" />
        </button>
        <button
          type="button"
          className="release-timeline__nav release-timeline__nav--next"
          onClick={() => goTo(next)}
          disabled={!next}
          aria-label={
            next ? `Later quarter, ${toQuarter(next)}` : "No later quarter"
          }
        >
          <Chevron direction="right" />
        </button>
      </div>

      <div className="release-timeline__status" aria-live="polite">
        {isCurrent ? (
          <>
            <strong>{toQuarter(selected)}</strong>
            <span className="release-timeline__badge">current quarter</span>
          </>
        ) : (
          <span className="release-timeline__notice" role="status">
            You are viewing {toQuarter(selected)}
            {" — "}
            <button
              type="button"
              className="release-timeline__back"
              onClick={() => setSearchParamsState({ release: undefined })}
            >
              back to the current quarter ({toQuarter(current)})
            </button>
          </span>
        )}
      </div>
    </div>
  );
};

export default ReleaseTimeline;
